import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ChatBotStyleAndConfig } from "../../../components/modals/style-and-config/style-config";
import { AppConfigService } from "../app-config/app.config.service";
import { AppConfig } from "../../../components/modals/app-config/app.config";
import { environment } from "../../../../environments/environment";
import { SessionService } from "../../../components/services/app-state-services/session/session.service";
import { RuntimeConfigService } from "../runtime-config/runtime-config.service";
import { TenantResolveService } from "../tenant/tenant-resolve.service";

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  private baseAssetUrl: string;
  private configLoadError = new BehaviorSubject<boolean>(false);
  public configLoadError$ = this.configLoadError.asObservable();
  // Distinct from configLoadError: the backend refused this tenant outright, so retrying or
  // degrading to default config cannot help. Kept separate so a network blip still degrades.
  private tenantRejected = new BehaviorSubject<boolean>(false);
  public tenantRejected$ = this.tenantRejected.asObservable();
  private configLoading = new BehaviorSubject<boolean>(true);
  public configLoading$ = this.configLoading.asObservable();
  private hasLoadedOnce = false;

  defaultConfigs: ChatBotStyleAndConfig = {
    companyLogo: "assets/images/titan_dms_company_logo.svg",
    chatBotIcon: "assets/images/titan_dms_bot_icon.svg",
    userIcon: "assets/images/usericon.png",
    humanAgentIcon: "assets/images/human_agent_icon.svg",
    landingPageImage: "assets/images/bot_landing_page_image.png",
    emojiPickerIcon: "assets/images/emoji_picker_icon.svg",
    sendBtnIcon: "assets/images/send_btn_icon.svg",
    botWidgetIcon: "assets/images/titan_bot_widget_icon.svg",
    startChatBtnIcon: "assets/images/start_chat_btn_icon.svg",
    chatBotBgColor: "#FFFFFF",
    primaryColor: "#F4801F",
    secondaryColor: "#1f2937",
    primaryTextColor: "#000000",
    secondaryTextColor: "#FFFFFF",
    queryPlaceholder: "Write a message",
    userBubbleColor: "#FDECDD",
    botBubbleColor: "#F2F3F3",
    textUserBubbleColor: "#0C0E0F",
    textBotBubbleColor: "#000E08",
    botIconColor: "#F4801F",
    textAreaColor: "#4C4C4E",
    textNotificationColor: "#4C4C4E",
    startChatBtnDisabledColor: "#FAC69A",
    colorDefault: "#4C4C4E",
    tenantCode: "",
    conversationAttachmentEnabled: "false",
    smsConversationChannelEnabled: "false",
  };

  configStyleConfigs: ChatBotStyleAndConfig = {};

  private configInitialState: ChatBotStyleAndConfig = this.defaultConfigs;

  private configStateSubject = new BehaviorSubject<ChatBotStyleAndConfig>(
    this.configInitialState
  );
  chatBotStyleAndConfig$ = this.configStateSubject.asObservable();

  constructor(
    private _appConfigService: AppConfigService,
    private _sessionService: SessionService,
    private _runtimeConfigService: RuntimeConfigService,
    private _tenantResolveService: TenantResolveService
  ) {
    this.baseAssetUrl = this.getBaseAssetUrl();
    this.updateDefaultConfigsWithBaseUrl();
  }

  getBaseAssetUrl(): string {
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src;
      if (src && src.indexOf("main.js") !== -1) {
        const url = src.substring(0, src.lastIndexOf("/") + 1);
        this._sessionService.setBaseAssetUrl(url);
        return url;
      }
    }
    this._sessionService.setBaseAssetUrl("/");
    return "/";
  }

  private updateDefaultConfigsWithBaseUrl(): void {
    const imageKeys: (keyof ChatBotStyleAndConfig)[] = [
      "companyLogo",
      "chatBotIcon",
      "userIcon",
      "humanAgentIcon",
      "landingPageImage",
      "emojiPickerIcon",
      "sendBtnIcon",
      "botWidgetIcon",
      "startChatBtnIcon",
    ];

    imageKeys.forEach((key) => {
      const val = this.defaultConfigs[key];
      if (typeof val === "string" && val.startsWith("assets/")) {
        this.defaultConfigs[key] = this.baseAssetUrl + val;
      }
    });
  }

  loadConfig(): void {
    if (this.hasLoadedOnce) {
      this.configLoading.next(false);
      return;
    }
    this.configLoading.next(true);
    this._tenantResolveService.ensureTenantCode().subscribe(() => {
      this.loadAppConfigs();
    });
  }

  setCustomizedStylingAndConfig(config: ChatBotStyleAndConfig): void {
    this.configStateSubject.next(config);
  }

  updateCssVariables(configs: ChatBotStyleAndConfig): void {
    const root = document.documentElement;
    Object.entries(configs).forEach(([key, value]) => {
      const cssVariableName = `--${key}`;
      if (typeof value === "string" && value.startsWith("#")) {
        root.style.setProperty(cssVariableName, value);
      }
    });
  }

  getCurrentConfig(): ChatBotStyleAndConfig {
    return this.configStateSubject.getValue();
  }

  loadAppConfigs() {
    this._appConfigService.getAppConfigs().subscribe({
      next: (response: AppConfig[]) => {
        this._runtimeConfigService.applyFromAppConfigs(response);
        this.mapToChatBotStyleConfig(response);
        this.configLoadError.next(false);
        this.configLoading.next(false);
        this.hasLoadedOnce = true;
      },
      error: (error: any) => {
        // 403 means the tenant is switched off (or its dealer is). The stored code is dead, so drop
        // it — a reload then resolves by host instead of replaying a code the backend keeps refusing.
        if (error?.status === 403) {
          this._sessionService.setTenantCode("");
          this.tenantRejected.next(true);
        }
        this.defaultConfigs.tenantCode = this._sessionService.getTenantCode() ?? "";
        this.updateCssVariables(this.defaultConfigs);
        this.configStateSubject.next({ ...this.defaultConfigs });
        this.configLoadError.next(true);
        this.configLoading.next(false);
        console.error("Error loading app configs:", error);
      },
    });
  }

  mapToChatBotStyleConfig(configStyles: AppConfig[]) {
    configStyles.forEach((config) => {
      const key = this.snakeToCamel(config.name) as keyof ChatBotStyleAndConfig;
      if (config.type === "IMAGE_ID") {
        const imageUrl = `${environment.apiURL}api/app-configs/asset/${environment.componentType}/${config.name}`;
        this.defaultConfigs[key] = imageUrl;
      } else if (config.name === "CONVERSATION_ATTACHMENT_ENABLED") {
        this.defaultConfigs.conversationAttachmentEnabled =
          config.value !== "false" ? "true" : "false";
      } else {
        this.defaultConfigs[key] = config.value;
      }
    });
    this.defaultConfigs.tenantCode = this._sessionService.getTenantCode() ?? "";
    this.updateCssVariables(this.defaultConfigs);
    this.configStateSubject.next({ ...this.defaultConfigs });
  }

  camelToSnake(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, "$1_$2").toUpperCase();
  }

  snakeToCamel(str: string): string {
    return str
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }
}